import { AdminLayout } from '@/components/admin/AdminLayout';
import { StatCard } from '@/components/admin/StatCard';
import { UserGrowthChart, PlanDistributionChart } from '@/components/admin/Charts';
import { useAdminStats } from '@/hooks/useAdminStats';
import { useAdminChartData } from '@/hooks/useAdminChartData';
import { Users, UserCheck, UserPlus, Crown, Briefcase, Clock } from 'lucide-react';

export const AdminDashboard = () => {
  const { stats, loading: statsLoading } = useAdminStats();
  const { monthlyData, loading: chartLoading } = useAdminChartData();

  if (statsLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* ページヘッダー */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">ダッシュボード</h1>
          <p className="text-gray-600 mt-1">システム全体の統計情報</p>
        </div>

        {/* 統計カードグリッド */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard
            title="総ユーザー数"
            value={stats.totalUsers}
            icon={Users}
            trend={stats.userGrowth}
            color="blue"
          />
          
          <StatCard
            title="アクティブユーザー"
            value={stats.activeUsers}
            icon={UserCheck}
            trend={stats.activeGrowth}
            color="green"
          />
          
          <StatCard
            title="無料プラン"
            value={stats.freeUsers}
            icon={UserPlus}
            color="orange"
          />
          
          <StatCard
            title="Proプラン"
            value={stats.proUsers}
            icon={Crown}
            color="purple"
          />
          
          <StatCard
            title="総プロジェクト数"
            value={stats.totalProjects || 0}
            icon={Briefcase}
            color="indigo"
          />
          
          <StatCard
            title="総稼働時間"
            value={`${stats.totalWorkingHours || 0}h`}
            icon={Clock}
            color="pink"
          />
        </div>

        {/* グラフセクション */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ユーザー成長グラフ */}
          <div className="card">
            <h2 className="text-lg font-bold text-gray-900 mb-4">ユーザー成長</h2>
            {chartLoading ? (
              <div className="h-[300px] flex items-center justify-center bg-gray-50 rounded-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <UserGrowthChart data={monthlyData} />
            )}
          </div>

          {/* プラン別ユーザー分布 */}
          <div className="card">
            <h2 className="text-lg font-bold text-gray-900 mb-4">プラン別ユーザー分布</h2>
            {chartLoading ? (
              <div className="h-[300px] flex items-center justify-center bg-gray-50 rounded-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <PlanDistributionChart data={monthlyData} />
            )}
          </div>
        </div>

        {/* システムヘルス */}
        <div className="card">
          <h2 className="text-lg font-bold text-gray-900 mb-4">システムヘルス</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-900">Firebase</span>
                <span className="text-xs font-bold text-green-600">正常</span>
              </div>
              <div className="mt-2 h-2 bg-green-200 rounded-full overflow-hidden">
                <div className="h-full w-full bg-green-500"></div>
              </div>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-900">Firestore</span>
                <span className="text-xs font-bold text-green-600">正常</span>
              </div>
              <div className="mt-2 h-2 bg-green-200 rounded-full overflow-hidden">
                <div className="h-full w-full bg-green-500"></div>
              </div>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-900">Storage</span>
                <span className="text-xs font-bold text-green-600">正常</span>
              </div>
              <div className="mt-2 h-2 bg-green-200 rounded-full overflow-hidden">
                <div className="h-full w-full bg-green-500"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};
